import React, { useEffect, useState } from "react";
import { Table, Input, Button, message, Tag, Select, Form } from "antd";
import { DownloadOutlined, FilterOutlined } from "@ant-design/icons";
import { Helmet } from "react-helmet";
import { Typography } from "antd";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import RequirePermission from "../components/Common/RequirePermission";
const { Title } = Typography;

const Locations = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const [cityOptions, setCityOptions] = useState([]);
  const [buildingOptions, setBuildingOptions] = useState([]);
  const [venueTypeOptions, setVenueTypeOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  const [selectedCity, setSelectedCity] = useState("");
  const [selectedBuilding, setSelectedBuilding] = useState("");
  const [selectedVenueType, setSelectedVenueType] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [filterText, setFilterText] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const setQueryParams = (params) => {
    const searchParams = new URLSearchParams(params);
    navigate(`?${searchParams.toString()}`, { replace: true });
  };

  const handleSearch = (e) => {
    setFilterText(e.target.value);
  };

  const handleCityChange = (e) => {
    const value = e;
    setSelectedCity(value || "");
    setQueryParams({
      city: value,
      building: selectedBuilding,
      venue_type: selectedVenueType,
      status: selectedStatus,
      filter: filterText,
    });
  };

  const handleBuildingChange = (e) => {
    const value = e;
    setSelectedBuilding(value || "");
    setQueryParams({
      city: selectedCity,
      building: value,
      venue_type: selectedVenueType,
      status: selectedStatus,
      filter: filterText,
    });
  };

  const handleVenueTypeChange = (e) => {
    const value = e;
    setSelectedVenueType(value || "");
    setQueryParams({
      city: selectedCity,
      building: selectedBuilding,
      venue_type: value,
      status: selectedStatus,
      filter: filterText,
    });
  };

  const handleStatusChange = (e) => {
    const value = e;
    setSelectedStatus(value || "");
    setQueryParams({
      city: selectedCity,
      building: selectedBuilding,
      venue_type: selectedVenueType,
      status: value,
      filter: filterText,
    });
  };

  const handleClearFilters = () => {
    setFilterText("");
    setSelectedCity("");
    setSelectedBuilding("");
    setSelectedVenueType("");
    setSelectedStatus("");
    setQueryParams({
      city: "",
      building: "",
      venue_type: "",
      status: "",
      filter: "",
    });
  };

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/locations/masters`
      );

      const cities = response.data.data.cities;
      const buildings = response.data.data.buildings || [];
      const venueTypes = response.data.data.venue_types || [];
      const statuses = response.data.data.status || [];

      setCityOptions(
        cities.map((city) => {
          return {
            value: city.id,
            label: city.name,
          };
        })
      );
      setBuildingOptions(
        buildings.map((building) => ({
          value: building.id,
          label: building.name,
        }))
      );
      setVenueTypeOptions(venueTypes);
      setStatusOptions(statuses);
      if (
        queryParams.get("city") &&
        queryParams.get("city") !== "" &&
        !isNaN(queryParams.get("city"))
      ) {
        setSelectedCity(parseInt(queryParams.get("city")));
      }
      if (
        queryParams.get("building") &&
        queryParams.get("building") !== "" &&
        !isNaN(queryParams.get("building"))
      ) {
        setSelectedBuilding(parseInt(queryParams.get("building")));
      }
      if (queryParams.get("venue_type")) {
        setSelectedVenueType(queryParams.get("venue_type"));
      }
      if (queryParams.get("status")) {
        setSelectedStatus(queryParams.get("status"));
      }
      if (
        queryParams.get("city") ||
        queryParams.get("building") ||
        queryParams.get("venue_type") ||
        queryParams.get("status")
      ) {
        setShowFilters(true);
      }

      setLoading(false);
    } catch (err) {
      console.error(err.message);
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + `/api/locations/grouped`,
        {
          params: {
            filter: filterText,
            city: selectedCity,
            building: selectedBuilding,
            venue_type: selectedVenueType,
            status: selectedStatus,
          },
        }
      );
      setLocations(response.data.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching locations data", error);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + `/api/locations/download`,
        {
          params: {
            filter: filterText,
            city: selectedCity,
            building: selectedBuilding,
            venue_type: selectedVenueType,
            status: selectedStatus,
          },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "locations.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setDownloading(false);
    } catch (error) {
      setDownloading(false);
      message.error("Failed to download locations");
    }
  };

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    setQueryParams({
      city: selectedCity,
      building: selectedBuilding,
      venue_type: selectedVenueType,
      status: selectedStatus,
      filter: filterText,
    });
    const fetchTimer = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(fetchTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterText, selectedCity, selectedBuilding, selectedVenueType, selectedStatus]);

  const columns = [
    { title: "Name", dataIndex: "name" },
    {
      title: "City",
      dataIndex: "city_name",
      render: (text) => <Tag>{text}</Tag>,
    },
    {
      title: "Code",
      dataIndex: "code",
      render: (text) => (text ? <Tag color={"blue"}>{text}</Tag> : null),
    },
    {
      title: "Venue Type",
      dataIndex: "venue_type",
      render: (text) =>
        text ? <Tag color={text === "Managed" ? "geekblue" : "orange"}>{text}</Tag> : null,
    },
    {
      title: "Audience Count",
      dataIndex: "audience_count",
      render: (value, record) => (
        <span style={{ fontWeight: record.children?.length > 0 ? 600 : 400 }}>
          {value ?? "-"}
        </span>
      ),
    },
    {
      title: "Device Count",
      dataIndex: "device_count",
      render: (value, record) => (
        <span style={{ fontWeight: record.children?.length > 0 ? 600 : 400 }}>
          {value ?? 0}
        </span>
      ),
    },
    {
      title: "Address",
      dataIndex: "address",
    },
    {
      title: "Status",
      dataIndex: "activated",
      render: (status) => (
        <Tag color={parseInt(status) === 1 ? "green" : "red"}>
          {parseInt(status) === 1 ? "Active" : "Inactive"}
        </Tag>
      ),
    },
  ];

  return (
    <RequirePermission permission="locations.view">
      <div style={{ padding: "1rem" }}>
        <Helmet>
          <title>{`${process.env.REACT_APP_NAME} | Locations`}</title>
        </Helmet>
        <Title level={3} style={{ marginBottom: "1rem" }}>
          Locations
        </Title>
        <div className="d-sm-block d-md-flex justify-content-between mb-3">
          <Form layout="inline">
            <Form.Item>
              <Input.Search
                placeholder="Search location"
                value={filterText}
                className="me-1"
                onChange={handleSearch}
                style={{ width: 200 }}
              />
            </Form.Item>
            <Form.Item>
              <Button
                icon={<FilterOutlined />}
                type={showFilters ? "primary" : "default"}
                onClick={() => setShowFilters((prev) => !prev)}
              >
                Filters
                {(selectedCity || selectedBuilding || selectedVenueType || selectedStatus) && (
                  <Tag color="blue" style={{ marginLeft: 6, marginRight: 0 }}>
                    {[selectedCity, selectedBuilding, selectedVenueType, selectedStatus].filter(Boolean).length}
                  </Tag>
                )}
              </Button>
            </Form.Item>
          </Form>
          <Button icon={<DownloadOutlined />} onClick={handleDownload} loading={downloading}>
            Download Excel
          </Button>
        </div>

        {showFilters && (
          <Form layout="inline" className="mb-3">
            <Form.Item label="City">
              <Select
                value={selectedCity}
                onChange={(e) => handleCityChange(e)}
                style={{ width: 200 }}
                showSearch
                filterOption={(input, option) => {
                  return (option?.children ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase());
                }}
                allowClear
              >
                {cityOptions.map((city) => (
                  <Select.Option value={city.value} key={city.value}>
                    {city.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Building">
              <Select
                value={selectedBuilding}
                onChange={(e) => handleBuildingChange(e)}
                style={{ width: 220 }}
                showSearch
                filterOption={(input, option) => {
                  return (option?.children ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase());
                }}
                allowClear
              >
                {buildingOptions.map((building) => (
                  <Select.Option value={building.value} key={building.value}>
                    {building.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Venue Type">
              <Select
                value={selectedVenueType}
                onChange={(e) => handleVenueTypeChange(e)}
                style={{ width: 160 }}
                allowClear
              >
                {venueTypeOptions.map((venueType) => (
                  <Select.Option value={venueType.value} key={venueType.value}>
                    {venueType.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Status">
              <Select
                value={selectedStatus}
                onChange={(e) => handleStatusChange(e)}
                style={{ width: 140 }}
                allowClear
              >
                {statusOptions.map((status) => (
                  <Select.Option value={status.value} key={status.value}>
                    {status.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item>
              <Button onClick={handleClearFilters}>Clear Filters</Button>
            </Form.Item>
          </Form>
        )}

        <Table
          loading={loading}
          columns={columns}
          dataSource={locations}
          scroll={{ x: "max-content" }}
          rowKey={(record) => record.id}
          pagination={{
            defaultPageSize: 20,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} buildings`,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
        />
      </div>
    </RequirePermission>
  );
};

export default Locations;
